// First-party marketing attribution — capture where a buyer came from.
//
// On a *campaigned* landing (URL carries any utm_* param or X's `twclid` click
// id) we persist that attribution to localStorage as the "last campaigned
// touch", along with the external referrer and the landing path. Plain internal
// navigation never overwrites it, so the signal survives until the buyer checks
// out, at which point the order-create request forwards these fields and the
// server stores them on the order (visible in the admin).
//
// Last-touch (not first-touch) because the question we care about is "which ad /
// campaign drove this sale" — the most recent campaigned click before purchase.

const STORAGE_KEY = 'raspicks:attribution';

// Don't credit a purchase to a campaign click older than this.
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

// Stripe metadata caps values at 500 chars; keep everything well under.
const MAX_LEN = 400;

export type Attribution = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  twclid?: string;
  referrer?: string;
  landingPage?: string;
};

type StoredAttribution = Attribution & { at: string };

const clip = (value: string | null | undefined): string | undefined => {
  const trimmed = (value || '').trim();
  return trimmed ? trimmed.slice(0, MAX_LEN) : undefined;
};

/**
 * Read the URL for campaign signals. Returns the attribution fields present, or
 * null when this isn't a campaigned landing (so we leave stored data untouched).
 */
function readIncoming(): Attribution | null {
  if (typeof window === 'undefined') { return null; }

  const params = new URLSearchParams(window.location.search);
  const utmSource = clip(params.get('utm_source'));
  const utmMedium = clip(params.get('utm_medium'));
  const utmCampaign = clip(params.get('utm_campaign'));
  const utmTerm = clip(params.get('utm_term'));
  const utmContent = clip(params.get('utm_content'));
  const twclid = clip(params.get('twclid'));

  // Only treat this as an attributable visit if a campaign signal is present.
  if (!utmSource && !utmMedium && !utmCampaign && !utmTerm && !utmContent && !twclid) {
    return null;
  }

  // External referrer only — internal (same-host) referrers aren't a source.
  let referrer: string | undefined;
  try {
    const ref = typeof document !== 'undefined' ? document.referrer : '';
    if (ref && new URL(ref).hostname !== window.location.hostname) {
      referrer = clip(ref);
    }
  } catch {
    referrer = undefined;
  }

  const landingPage = clip(`${window.location.pathname}${window.location.search}`);

  return {
    utmSource,
    utmMedium,
    utmCampaign,
    utmTerm,
    utmContent,
    twclid,
    referrer,
    landingPage,
  };
}

/**
 * Call once per client render. If the current URL is a campaigned landing,
 * store it (overwriting any prior campaigned touch). No-op otherwise, so normal
 * in-app navigation never clears the attribution.
 */
export function captureAttribution(): void {
  try {
    const incoming = readIncoming();
    if (!incoming) { return; }

    const record: StoredAttribution = { ...incoming, at: new Date().toISOString() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage can throw (private mode, quota); attribution is best-effort.
  }
}

/**
 * Read the stored attribution for attaching to an order-create request. Returns
 * only defined fields (safe to spread into a request body), or {} when there's
 * no fresh campaigned touch.
 */
export function getAttribution(): Attribution {
  try {
    if (typeof window === 'undefined') { return {}; }

    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) { return {}; }

    const stored = JSON.parse(raw) as StoredAttribution;
    if (stored?.at && Date.now() - new Date(stored.at).getTime() > MAX_AGE_MS) {
      return {}; // stale — don't attribute an old click to this purchase
    }

    const { at: _at, ...fields } = stored || {};
    // Drop any undefined/empty keys so the request body stays clean.
    return Object.fromEntries(
      Object.entries(fields).filter(([, v]) => typeof v === 'string' && v),
    ) as Attribution;
  } catch {
    return {};
  }
}
